import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery, clientMutation } from "../../../../../../../core/data/GraphQLApi";
import { endpoints } from "../../../../../../../core/defaultValues";
import { ExceptionManager } from "../../../../../../../core/logManager";
import { resetALL } from "../../../../../../../.globals/redux/actions";

const INIT_STATE = {
    loading: false,
    dinamicForms: [],
    open: false,
    create: null,
    update: null,
    workOrderType: null,
    workOrderTypeId: null
};

const route = "modules/settings/views/workOrderType/modals/createEditManageWorkOrderType/redux/slice.";

export const manageWorkOrderTypeSearchDynamicForms = createAsyncThunk("manage/createEditWorkOrderType/searchDynamicForms",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                query ($conditions: [DynamicFormSearchInput], $sort: [DynamicFormSortInput], $limit: Int, $offset: Int) {
                    data: searchDynamicForms(
                        conditions: $conditions
                        sort: $sort
                        limit: $limit
                        offset: $offset
                    ) {
                        total
                        items {
                            id
                            name
                            formStatus
                            category
                        } 
                    }
                }
                `,
                {
                    "conditions": [
                        {
                            fields: ["formStatus"],
                            terms: ["active"],
                            exact: true
                        },
                        {
                            fields: ["category"],
                            terms: ["WorkOrder"],
                            exact: true
                        }
                    ],
                    "sort": [{ field: "name", order: "ASC" }],
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data || { items: [], total: 0 };
        } catch (exc) {
            ExceptionManager(exc, route, "searchDynamicForms");
            return rejectWithValue(exc);
        }
    }
);

export const manageWorkOrderTypeCreateWorkOrderType = createAsyncThunk("manage/createEditWorkOrderType/createWorkOrderType",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientMutation(
                `
                    mutation createWorkOrderType ($name: String!, $description: String, $dynamicFormId: Int, $estimatedDurationMins: Int) {
                        result: createWorkOrderType (name: $name, description: $description, dynamicFormId: $dynamicFormId, estimatedDurationMins: $estimatedDurationMins) {
                            id
                            name
                            dynamicFormId
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.result
        } catch (exc) {
            ExceptionManager(exc, route, "createWorkOrderType");
            return rejectWithValue(exc);
        }
    }
);

export const manageWorkOrderTypeEditWorkOrderType = createAsyncThunk("manage/createEditWorkOrderType/updateWorkOrderType",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientMutation(
                `
                    mutation updateWorkOrderType ($id: Int!, $name: String, $description: String, $dynamicFormId: Int, $estimatedDurationMins: Int) {
                        result: updateWorkOrderType (id: $id, name: $name, description: $description, dynamicFormId: $dynamicFormId, estimatedDurationMins: $estimatedDurationMins) {
                            id
                            name
                            dynamicFormId
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.result
        } catch (exc) {
            ExceptionManager(exc, route, "updateWorkOrderType");
            return rejectWithValue(exc);
        }
    }
);

export const manageWorkOrderTypeGetWorkOrderType = createAsyncThunk("manage/createEditWorkOrderType/getWorkOrderType",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                    query getWorkOrderType ($id: Int!) {
                        result: getWorkOrderType (id: $id) {
                            id
                            name
                            description
                            dynamicFormId
                            dynamicForm {
                                id
                                name
                            }
                            estimatedDurationMins
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.result || null;
        } catch (exc) {
            ExceptionManager(exc, route, "getWorkOrderType");
            return rejectWithValue(exc);
        }
    }
);

//Slice
const manageCreateEditWorkOrderTypeRedux = createSlice({
    name: "manage/createEditWorkOrderType",
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
        openModalCreateEditWorkOrderType(state, action) {
            state.open = true;
            state.workOrderTypeId = action?.payload || null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //manageWorkOrderTypeSearchDynamicForms
        builder.addCase(manageWorkOrderTypeSearchDynamicForms.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(manageWorkOrderTypeSearchDynamicForms.fulfilled, (state, action) => {
            state.loading = false;
            state.dinamicForms = action.payload.items;
        });
        builder.addCase(manageWorkOrderTypeSearchDynamicForms.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //manageWorkOrderTypeCreateWorkOrderType
        builder.addCase(manageWorkOrderTypeCreateWorkOrderType.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(manageWorkOrderTypeCreateWorkOrderType.fulfilled, (state, action) => {
            state.loading = false;
            state.create = action.payload;
        });
        builder.addCase(manageWorkOrderTypeCreateWorkOrderType.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //manageWorkOrderTypeEditWorkOrderType
        builder.addCase(manageWorkOrderTypeEditWorkOrderType.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(manageWorkOrderTypeEditWorkOrderType.fulfilled, (state, action) => {
            state.loading = false;
            state.update = action.payload;
        });
        builder.addCase(manageWorkOrderTypeEditWorkOrderType.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //manageWorkOrderTypeGetWorkOrderType
        builder.addCase(manageWorkOrderTypeGetWorkOrderType.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(manageWorkOrderTypeGetWorkOrderType.fulfilled, (state, action) => {
            state.loading = false;
            state.workOrderType = action.payload;
        });
        builder.addCase(manageWorkOrderTypeGetWorkOrderType.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = manageCreateEditWorkOrderTypeRedux;

// Extract and export each action creator by name
export const { reset, openModalCreateEditWorkOrderType } = actions;

// Export the reducer, either as a default or named export
export default reducer;